// ICIC_POSTER_APP_INTEGRATION_V1
import {
  isNativeApp
} from './nativeApp';

import {
  getNativeDeviceToken
} from './nativeDeviceAuth';


const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;

const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;


async function actorCredentials(
  session,
  actorType
) {
  if (
    actorType ===
    'instructor'
  ) {
    if (!session?.cmsToken) {
      throw new Error(
        'The Instructor session is unavailable.'
      );
    }

    return {
      actorType:
        'instructor',

      cmsToken:
        session.cmsToken
    };
  }


  if (
    session?.isMaster &&
    session?.masterSessionToken
  ) {
    return {
      actorType:
        'student',

      masterSessionToken:
        session.masterSessionToken
    };
  }


  if (isNativeApp()) {
    const deviceToken =
      await getNativeDeviceToken();

    if (!deviceToken) {
      throw new Error(
        'The saved Student app login is unavailable.'
      );
    }

    return {
      actorType:
        'student',

      deviceToken
    };
  }


  if (
    session?.webSessionToken
  ) {
    return {
      actorType:
        'student',

      webSessionToken:
        session.webSessionToken
    };
  }


  throw new Error(
    'The Student session is unavailable.'
  );
}


async function callBridge(
  session,
  actorType,
  action,
  extra = {}
) {
  const credentials =
    await actorCredentials(
      session,
      actorType
    );


  const response =
    await fetch(
      SUPABASE_URL +
        '/functions/v1/icic-bridge',
      {
        method:
          'POST',

        headers: {
          apikey:
            SUPABASE_KEY,

          Authorization:
            'Bearer ' +
            SUPABASE_KEY,

          'Content-Type':
            'application/json'
        },

        body:
          JSON.stringify({
            ...credentials,
            action,
            ...extra
          })
      }
    );


  const text =
    await response.text();


  let result =
    null;

  if (text) {
    try {
      result =
        JSON.parse(text);
    } catch {
      result =
        null;
    }
  }


  if (
    !response.ok ||
    result?.ok === false
  ) {
    throw new Error(
      result?.message ||
      'ICIC could not be loaded.'
    );
  }


  return result;
}


export async function
loadIcicConferenceState(
  session,
  actorType
) {
  const result =
    await callBridge(
      session,
      actorType,
      'conference_state'
    );

  return (
    result?.state ||
    null
  );
}


export async function
loadIcicPortal(
  session,
  actorType
) {
  const result =
    await callBridge(
      session,
      actorType,
      'portal'
    );

  return (
    result?.portal ||
    null
  );
}


export async function loadIcicCompetitionState(session, actorType) {
  const result = await callBridge(
    session,
    actorType,
    'competition_state'
  );
  return result?.competition || null;
}


export async function loadIcicPosterState(session) {
  const result = await callBridge(
    session,
    'student',
    'poster_state'
  );
  return result?.state || null;
}


export async function submitIcicPoster(session, proposal) {
  return await callBridge(
    session,
    'student',
    'submit_poster',
    { proposal }
  );
}


export async function
saveIcicSession(
  session,
  actorType,
  sessionId
) {
  const result =
    await callBridge(
      session,
      actorType,
      'save_session',
      {
        sessionId
      }
    );

  return Array.isArray(
    result?.savedSessionIds
  )
    ? result.savedSessionIds
    : [];
}


export async function
unsaveIcicSession(
  session,
  actorType,
  sessionId
) {
  const result =
    await callBridge(
      session,
      actorType,
      'unsave_session',
      {
        sessionId
      }
    );

  return Array.isArray(
    result?.savedSessionIds
  )
    ? result.savedSessionIds
    : [];
}


export async function loadIcicScheduleItems(session, actorType) {
  const [portal, posterState] = await Promise.all([
    loadIcicPortal(session, actorType),
    actorType === 'student'
      ? loadIcicPosterState(session)
      : Promise.resolve(null)
  ]);

  const catalogue = portal?.catalogue || {};
  const myIds = new Set(
    (Array.isArray(portal?.mySessionIds) ? portal.mySessionIds : []).map(String)
  );
  const savedIds = new Set(
    (Array.isArray(portal?.savedSessionIds) ? portal.savedSessionIds : []).map(String)
  );

  const items = (
    catalogue?.available && Array.isArray(catalogue.schedule)
      ? catalogue.schedule
      : []
  )
    .filter(row => {
      const id = String(row?.id || '');
      return myIds.has(id) || savedIds.has(id);
    })
    .map(row => {
      const id = String(row.id);
      return {
        ...row,
        isMySession: myIds.has(id),
        isSavedSession: savedIds.has(id)
      };
    });

  if (actorType !== 'student' || !posterState?.available) {
    return items;
  }

  const posters = Array.isArray(posterState.posters)
    ? posterState.posters
    : [];

  const consentOutstanding = posters.some(
    poster =>
      ['accepted', 'poster_received'].includes(poster.status) &&
      Array.isArray(poster.authors) &&
      poster.authors.some(
        author =>
          author?.isCurrentStudent === true &&
          author?.consentComplete !== true
      )
  );

  if (consentOutstanding && posterState?.deadlines?.consentDueOn) {
    items.push({
      id: 'poster-consent-' + posterState.conference.id,
      date: posterState.deadlines.consentDueOn,
      scheduleKind: 'deadline',
      title: 'ICIC Poster Consent',
      deadlineType: 'consent'
    });
  }

  if (posterState?.deadlines?.posterUploadDueOn) {
    posters
      .filter(
        poster =>
          poster.status === 'accepted' &&
          poster.withdrawn !== true &&
          poster?.upload?.received !== true
      )
      .forEach(poster => {
        items.push({
          id: 'poster-upload-' + poster.id,
          date: posterState.deadlines.posterUploadDueOn,
          scheduleKind: 'deadline',
          title:
            'ICIC Final Poster Upload' +
            (poster.entryCode ? ' — ' + poster.entryCode : ''),
          deadlineType: 'poster_upload',
          posterId: poster.id
        });
      });
  }

  return items;
}
